import { dataUsage } from "api/data";
import { DataReqBody } from "api/types/data";
import { Device } from "api/types/device";
import { useEffect, useState } from "react";

import { formatDate, formatDateToZ } from "util/datelib";
import DatePickerField from "widgets/shared/inputs/datePicker";
import PropagateLoader from "react-spinners/PropagateLoader";
import { t } from "i18next";
import { DeviceConfiguration } from "models/devices/configuration";
import { getChartColors } from "util/device";
import DeviceCard from "widgets/cards/device";
import NoDataSvg from "assets/svg/no-data.svg";
import BarGraph from "widgets/charts/bar";
import { ChartDataset } from "widgets/charts/models";
import { TimeSeriesChartDropdown } from "models/dropdowns/timeSeriesChart";
import GenericButton from "widgets/controls/buttons/generic";

// Define the props for the Role component
interface GeneralProps {
  device?: Device;
  config?: DeviceConfiguration;
  height?: number;
}

const UsageMulti: React.FC<GeneralProps> = ({ device, config, height }) => {
  const { color0, color3 } = getChartColors(config || undefined);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);

  const [chartData, setChartData] = useState<ChartDataset>({
    labels: [],
    datasets: [
      {
        label: "set0",
        data: [],
        backgroundColor: color0,
      },
    ],
  });

  const [date, setDate] = useState<any>(new Date());
  const [filter, setFilter] = useState<any>({
    calendar: "year",
    xFormat: "month",
    currentBucket: "day",
  });

  const setXFormat = (filter: string) => {
    if (filter === "month") {
      setDate(new Date());
      setFilter({ calendar: filter, xFormat: "day", currentBucket: "hour" });
    } else if (filter === "year") {
      setFilter({ calendar: filter, xFormat: "month", currentBucket: "day" });
    } else {
      setFilter({ calendar: filter, xFormat: "year", currentBucket: "month" });
    }
  };

  const fetchData = async () => {
    setLoading(true);

    const req: DataReqBody = {
      snr: device?.snr,
      from: formatDateToZ(date, false, filter.xFormat, device?.timezone),
      to: formatDateToZ(date, true, filter.xFormat, device?.timezone),
      bucket: filter.currentBucket,
      sensors: config.sensors.map((s) => s.key),
    };

    // Update state for date range
    setStartDate(formatDateToZ(date, false, filter.xFormat, device?.timezone));
    setEndDate(formatDateToZ(date, true, filter.xFormat, device?.timezone));

    const { data: newData, status, isLoading, error } = await dataUsage(req);
    setLoading(isLoading);

    if (!error && status && status === 200) {
      const labels = [];
      const set1 = [];
      const set2 = [];

      for (let index = 0; index < newData.length; index++) {
        const element = newData[index];
        const label = formatDate(
          filter.currentBucket === "hour" ? element.timestamp : element._id,
          filter.currentBucket
        );

        const usage =
          config.units === "water"
            ? element[config.sensors[0].key] / 1000
            : element[config.sensors[0].key];
        const usage2 =
          config.units === "water"
            ? element[config.sensors[1].key] / 1000
            : element[config.sensors[1].key];
        labels.push(label);
        set1.push(usage);
        set2.push(usage2);
      }

      // Create the data sets
      const dataSets = [];

      dataSets.push({
        label: t("device.tooltip.forward"),
        data: set1,
        backgroundColor: color0,
        borderWidth: 2,
        borderRadius: 5,
        borderSkipped: false,
      });

      dataSets.push({
        label: t("device.tooltip.reverse"),
        data: set2,
        backgroundColor: color3,
        borderWidth: 2,
        borderRadius: 5,
        borderSkipped: false,
      });

      setChartData({
        labels: labels,
        datasets: dataSets,
      });
    }
  };

  useEffect(() => {
    if (device?.snr) {
      fetchData();
    }
  }, [device, date, filter]);

  const getDescription = () => {
    if (filter.calendar === "year") {
      return `${formatDate(startDate, "ddMMMyyyy")} - ${formatDate(
        endDate,
        "ddMMMyyyy"
      )}`;
    } else if (filter.calendar === "decade") {
      return `${formatDate(startDate, "MMMyyyy")} - ${formatDate(
        endDate,
        "MMMyyyy"
      )}`;
    } else if (filter.calendar === "month") {
      return `${formatDate(startDate, "ddMMMyyyy")}`;
    }
    return "";
  };

  return (
    <DeviceCard
      title={t("chart.usage.title", {
        context: filter.calendar,
      })}
      subTitle={getDescription()}
      topRight={
        <>
          {TimeSeriesChartDropdown.map((item) => (
            <GenericButton
              key={item.value}
              text={item.label}
              onClick={() => setXFormat(item.value)}
              size="sm"
              color="50"
              variant={filter.calendar === item.value ? "solid" : "outline"}
              rounded="rounded-md"
              tooltip={t("tooltip.xformat.filter", { context: item.value })}
              tooltipPlacement="bottom"
            />
          ))}
          <DatePickerField
            id={"1"}
            sizes={"xs"}
            iconOnly
            value={date}
            onChange={(val) => {
              setDate(val); // Set new date and trigger useEffect
            }}
            maxDate={new Date()}
            maxDetail={filter.calendar}
          />
        </>
      }
    >
      <div className="h-full min-h-72 w-full overflow-x-auto">
        {loading ? (
          <div className="mt-12 flex items-center justify-center bg-opacity-50">
            <PropagateLoader loading={loading} color="var(--color-500)" />
          </div>
        ) : chartData.datasets.length > 0 &&
          chartData.datasets[0].data.length > 0 ? (
          <>
            <BarGraph
              id={"chartId"}
              data={chartData}
              config={config}
              height={height}
            />
          </>
        ) : (
          <div className="mt-4 flex justify-center">
            <img src={NoDataSvg} alt="Modal content" style={{ width: "12%" }} />
          </div>
        )}
      </div>
    </DeviceCard>
  );
};

export default UsageMulti;
